import React, { useCallback, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { useAgentInput, type PendingSkillMeta } from '../../../hooks/home/useAgentInput';
import { useClickOutside } from '@/hooks/useClickOutside';
import { MascotAvatar } from '../shared/MascotAvatar';
import { AgentModelSelector } from '../../agent/AgentModelSelector';
import { SkillPickerPanel } from './SkillPickerPanel';
import { SkillTag } from './SkillTag';
import type { SkillInfo } from '@/types/agent';

interface AgentInputBoxProps {
  onSend: (value: string, skill?: PendingSkillMeta) => void;
  greeting?: string;
  placeholder?: string;
}

export const AgentInputBox: React.FC<AgentInputBoxProps> = ({
  onSend,
  greeting,
  placeholder = '说出你的创意，BongAI 帮你在画布实现...',
}) => {
  const [selectedSkill, setSelectedSkill] = useState<SkillInfo | null>(null);
  const getSkill = useCallback(
    () => (selectedSkill ? { id: selectedSkill.id, name: selectedSkill.name } : null),
    [selectedSkill]
  );
  const clearSelectedSkill = useCallback(() => setSelectedSkill(null), []);

  const { value, isSending, setValue, setIsComposing, send, handleKeyDown } = useAgentInput(
    onSend,
    getSkill,
    clearSelectedSkill
  );

  const [showModeMenu, setShowModeMenu] = useState(false);
  const [showSkillPanel, setShowSkillPanel] = useState(false);
  const [mode, setMode] = useState<'auto' | 'manual'>('manual');
  const skillAnchorRef = useRef<HTMLDivElement>(null);
  const modeAnchorRef = useRef<HTMLDivElement>(null);

  // 点击模式菜单外部关闭（document 级监听）
  const closeModeMenu = useCallback(() => setShowModeMenu(false), []);
  useClickOutside([modeAnchorRef], closeModeMenu, showModeMenu);

  // 选中 Skill：以标签形式展示，不直接写入输入框文本
  const handleSkillSelect = (skill: SkillInfo) => {
    setSelectedSkill(skill);
    setShowSkillPanel(false);
  };

  const period = getPeriod();

  const iconBtnBase = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    padding: 0,
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease',
  } as const;

  const onHover = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.backgroundColor = '#3d3d3d'; };
  const onLeave = (e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.backgroundColor = 'transparent'; };

  const canSend = value.trim() || selectedSkill;

  return (
    /* 设计稿 327:482：对话框 1200×148 */
    <div className="w-full max-w-[1200px] mx-auto">
      <div className="flex items-center justify-center gap-2.5 mb-3" style={{ paddingLeft: '48px' }}>
        <span
          className="text-white"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '24px',
            lineHeight: '24px',
          }}
        >
          Hi~
        </span>
        <MascotAvatar size={64} />
        <span
          className="text-white"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '24px',
            lineHeight: '24px',
          }}
        >
          {greeting ?? `${period}好！`}
        </span>
      </div>

      <div
        className="relative transition-all border"
        style={{
          backgroundColor: '#222222',
          borderColor: '#363636',
          borderRadius: '16px',
          height: '148px',
          width: '100%',
        }}
      >
        {/* 已选技能标签 */}
        {selectedSkill && (
          <div style={{ position: 'absolute', top: '12px', left: '16px', zIndex: 10 }}>
            <SkillTag skill={selectedSkill} onRemove={() => setSelectedSkill(null)} />
          </div>
        )}

        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          onKeyDown={handleKeyDown}
          placeholder={selectedSkill ? '' : placeholder}
          rows={1}
          className="w-full bg-transparent resize-none outline-none placeholder:text-white/45"
          style={{
            color: '#ffffff',
            fontSize: '14px',
            lineHeight: '14px',
            fontFamily: '"PingFang SC", sans-serif',
            padding: selectedSkill ? '48px 56px 48px 16px' : '24px 56px 48px 16px',
            height: '148px',
            caretColor: '#ffffff',
            transition: 'padding-top 0.15s ease',
          }}
        />

        {/* 底部工具栏（设计稿：图标组件框 x=16、底距 16，发送按钮右距 16） */}
        <div
          className="absolute flex items-center"
          style={{ left: '16px', bottom: '16px', right: '16px' }}
        >
          {/* 附件按钮 */}
          <button
            style={iconBtnBase}
            onMouseEnter={onHover}
            onMouseLeave={onLeave}
            title="附件"
          >
            <Plus size={16} strokeWidth={1.5} className="text-white" />
          </button>

          {/* Skill 按钮 + 选择弹窗（弹窗内部自带外部点击关闭） */}
          <div className="relative" ref={skillAnchorRef}>
            <button
              style={{ ...iconBtnBase, marginLeft: '4px', backgroundColor: showSkillPanel ? '#3d3d3d' : 'transparent' }}
              onMouseEnter={(e) => { if (!showSkillPanel) e.currentTarget.style.backgroundColor = '#3d3d3d'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = showSkillPanel ? '#3d3d3d' : 'transparent'; }}
              title="技能"
              onClick={() => setShowSkillPanel((v) => !v)}
            >
              <img src="/icons/skill.svg" alt="技能" width={16} height={16} style={{ objectFit: 'contain' }} draggable={false} />
            </button>

            {showSkillPanel && (
              <SkillPickerPanel
                onSelect={handleSkillSelect}
                onClose={() => setShowSkillPanel(false)}
                triggerRef={skillAnchorRef}
              />
            )}
          </div>

          <div style={{ width: '1px', height: '16px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '0 6px' }} />

          {/* 模型选择器 */}
          <AgentModelSelector />

          {/* 模式选择按钮（外部点击关闭由 useClickOutside 处理） */}
          <div className="relative" ref={modeAnchorRef}>
            <button
              onClick={() => setShowModeMenu(!showModeMenu)}
              style={{ ...iconBtnBase, marginLeft: '4px', backgroundColor: showModeMenu ? '#3d3d3d' : 'transparent' }}
              onMouseEnter={(e) => { if (!showModeMenu) e.currentTarget.style.backgroundColor = '#3d3d3d'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = showModeMenu ? '#3d3d3d' : 'transparent'; }}
              title="模式选择"
            >
              <img src={mode === 'auto' ? '/icons/mode-auto.svg' : '/icons/mode-manual.svg'} alt="模式" width={16} height={16} style={{ objectFit: 'contain' }} draggable={false} />
            </button>

            {showModeMenu && (
              <>
                <div
                  className="absolute bottom-full right-0 mb-2 z-50 overflow-hidden"
                  style={{
                    width: '256px',
                    backgroundColor: '#141414',
                    border: '1px solid #2d2d2d',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                  }}
                >
                  {[
                    { id: 'auto' as const, label: '自动模式', desc: 'Agent完全自动生成', icon: '/icons/mode-auto.svg' },
                    { id: 'manual' as const, label: '手动模式', desc: 'Agent在每次生成前询问', icon: '/icons/mode-manual.svg' },
                  ].map((m) => (
                    <button
                      key={m.id}
                      onClick={() => { setMode(m.id); setShowModeMenu(false); }}
                      className="w-full text-left transition-colors"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 16px',
                        backgroundColor: mode === m.id ? '#2d2d2d' : 'transparent',
                      }}
                      onMouseEnter={(e) => { if (mode !== m.id) e.currentTarget.style.backgroundColor = '#2d2d2d'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = mode === m.id ? '#2d2d2d' : 'transparent'; }}
                    >
                      <div className="flex items-center gap-3">
                        <img src={m.icon} alt="" width={18} height={18} style={{ objectFit: 'contain', flexShrink: 0 }} draggable={false} />
                        <div>
                          <div style={{ fontSize: '14px', color: '#ffffff', fontFamily: '"PingFang SC", sans-serif', lineHeight: '20px' }}>
                            {m.label}
                          </div>
                          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', fontFamily: '"PingFang SC", sans-serif', lineHeight: '18px' }}>
                            {m.desc}
                          </div>
                        </div>
                      </div>
                      {mode === m.id && (
                        <img src="/icons/check.svg" alt="选中" width={18} height={18} style={{ objectFit: 'contain', flexShrink: 0 }} draggable={false} />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* 发送按钮 */}
          <div className="flex-1" />
          <button
            onClick={send}
            disabled={!canSend || isSending}
            className="flex items-center justify-center transition-all disabled:cursor-not-allowed"
            style={{
              ...iconBtnBase,
              backgroundColor: '#ffffff',
              opacity: canSend || isSending ? 1 : 0.4,
            }}
            aria-label="发送"
          >
            <img src="/icons/send-arrow.svg" alt="发送" width={16} height={16} style={{ objectFit: 'contain' }} draggable={false} />
          </button>
        </div>
      </div>
    </div>
  );
};

function getPeriod(): string {
  const hour = new Date().getHours();
  if (hour >= 12 && hour < 14) return '中午';
  if (hour >= 14 && hour < 18) return '下午';
  if (hour >= 18) return '晚上';
  return '早上';
}
